import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ComplianceDashboard = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const complianceStatus = [
    {
      category: "Data Residency",
      status: "compliant",
      lastCheck: "2025-12-04T05:00:00",
      details: "All data stored in US-East region as configured"
    },
    {
      category: "HIPAA Compliance",
      status: "compliant",
      lastCheck: "2025-12-04T04:30:00",
      details: "BAA signed, encryption enabled, audit logs active"
    },
    {
      category: "ISO 27001",
      status: "compliant",
      lastCheck: "2025-12-03T18:00:00",
      details: "Annual certification valid until 2026-06-15"
    },
    {
      category: "GDPR Readiness",
      status: "warning",
      lastCheck: "2025-12-04T03:15:00",
      details: "2 data subject requests pending response"
    }
  ];

  const auditLogs = [
    {
      id: 1,
      timestamp: "2025-12-04T05:45:00",
      action: "User Role Changed",
      actor: "james.wilson@techcorp.com",
      target: "sarah.johnson@techcorp.com",
      details: "Role changed from Seeker to Licensed Professional",
      severity: "medium"
    },
    {
      id: 2,
      timestamp: "2025-12-04T05:30:00",
      action: "SSO Configuration Updated",
      actor: "james.wilson@techcorp.com",
      target: "Organization Settings",
      details: "Azure AD client ID updated",
      severity: "high"
    },
    {
      id: 3,
      timestamp: "2025-12-04T05:15:00",
      action: "Helper Credentials Verified",
      actor: "system",
      target: "michael.chen@techcorp.com",
      details: "Professional license verification completed",
      severity: "low"
    },
    {
      id: 4,
      timestamp: "2025-12-04T04:50:00",
      action: "Data Export Requested",
      actor: "emily.rodriguez@techcorp.com",
      target: "Personal Data",
      details: "User requested full data export under GDPR",
      severity: "medium"
    },
    {
      id: 5,
      timestamp: "2025-12-04T04:20:00",
      action: "Emergency Escalation",
      actor: "system",
      target: "Session #8472",
      details: "Crisis protocol activated, emergency services notified",
      severity: "critical"
    }
  ];

  const breachIncidents = [
    {
      id: 1,
      date: "2025-11-28",
      type: "Unauthorized Access Attempt",
      status: "resolved",
      severity: "medium",
      description: "Multiple failed login attempts detected from unknown IP address",
      resolution: "IP blocked, user notified, password reset enforced"
    }
  ];

  const getStatusColor = (status) => {
    const colors = {
      compliant: "text-success",
      warning: "text-warning",
      critical: "text-error"
    };
    return colors?.[status] || "text-muted-foreground";
  };

  const getStatusIcon = (status) => {
    const icons = {
      compliant: "CheckCircle2",
      warning: "AlertTriangle",
      critical: "XCircle"
    };
    return icons?.[status] || "Info";
  };

  const getSeverityBadge = (severity) => {
    const badges = {
      low: "bg-muted text-muted-foreground",
      medium: "bg-warning/10 text-warning",
      high: "bg-error/10 text-error",
      critical: "bg-error text-error-foreground"
    };
    return badges?.[severity] || badges?.low;
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredLogs = auditLogs?.filter(log =>
    log?.action?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    log?.actor?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    log?.details?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const handleExportLogs = () => {
    // Create CSV content
    const headers = ['Timestamp', 'Action', 'Actor', 'Details', 'Severity'];
    const csvContent = [
      headers?.join(','),
      ...filteredLogs?.map(log => [
        formatTimestamp(log?.timestamp),
        log?.action,
        log?.actor,
        log?.details,
        log?.severity
      ]?.join(','))
    ]?.join('\n');
    
    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit-logs-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    link?.click();
    window.URL?.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {complianceStatus?.map((item, index) => {
          const statusIcon = getStatusIcon(item?.status);
          const statusColor = getStatusColor(item?.status);
          return (
            <div key={index} className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-start justify-between mb-3">
                <Icon name={statusIcon} size={24} className={statusColor} />
                <span className={`text-xs font-medium ${statusColor}`}>
                  {item?.status?.toUpperCase()}
                </span>
              </div>
              <h4 className="text-sm font-semibold mb-2">{item?.category}</h4>
              <p className="text-xs text-muted-foreground mb-2">{item?.details}</p>
              <p className="text-xs text-muted-foreground">
                Last checked: {formatTimestamp(item?.lastCheck)}
              </p>
            </div>
          );
        })}
      </div>
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Icon name="FileText" size={24} className="text-primary" />
            <div>
              <h3 className="text-lg font-semibold">Audit Logs</h3>
              <p className="text-sm text-muted-foreground">{filteredLogs?.length} entries found</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Input
              type="search"
              placeholder="Search logs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full sm:w-64"
            />
            <Button
              variant="outline"
              iconName="Download"
              iconPosition="left"
              onClick={handleExportLogs}
            >
              Export
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Timestamp</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Action</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actor</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Details</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Severity</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs?.map((log) => (
                <tr key={log?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                  <td className="py-4 px-4">
                    <span className="text-sm text-data">{formatTimestamp(log?.timestamp)}</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-sm font-medium">{log?.action}</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-sm text-muted-foreground">{log?.actor}</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-sm text-muted-foreground">{log?.details}</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityBadge(log?.severity)}`}>
                      {log?.severity?.toUpperCase()}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center gap-3 mb-6">
          <Icon name="Shield" size={24} className="text-primary" />
          <div>
            <h3 className="text-lg font-semibold">Security Incidents</h3>
            <p className="text-sm text-muted-foreground">{breachIncidents?.length} incidents tracked</p>
          </div>
        </div>

        <div className="space-y-4">
          {breachIncidents?.map((incident) => (
            <div key={incident?.id} className="border border-border rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="text-sm font-semibold">{incident?.type}</h4>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityBadge(incident?.severity)}`}>
                      {incident?.severity?.toUpperCase()}
                    </span>
                    <span className="text-xs text-success">
                      {incident?.status?.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{incident?.description}</p>
                  <p className="text-sm text-foreground">
                    <span className="font-medium">Resolution:</span> {incident?.resolution}
                  </p>
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap ml-4">
                  {incident?.date}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ComplianceDashboard;